import React, { useRef } from "react";
import axios from "axios";
import * as Icon from "react-feather";
import { useHistory } from "react-router-dom";
import "./PlanControl.css";
import {
  CircularProgress,
  TextField,
  MenuItem,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Check,
  EditCalendar,
  EventAvailableOutlined,
} from "@mui/icons-material";

const PlanControl = () => {
  const history = useHistory();
  const url = process.env.REACT_APP_BASEURL;

  const [subscriptionData, setSubscriptionData] = React.useState(null);
  const [load, setLoad] = React.useState(true);

  const [searchInput, setSearchInput] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filterType, setFilterType] = React.useState("all"); // "all", "trial", "real"

  const [sortBy, setSortBy] = React.useState("ExpirationDate");
  const [order, setOrder] = React.useState("desc");

  const [editedDate, setEditedDate] = React.useState({});
  const [editingRow, setEditingRow] = React.useState(null);
  const [selectedTenantId, setSelectedTenantId] = React.useState(null);
  const [loadingConfirm, setLoadingConfirm] = React.useState(false);
  const [msgData, setMsgData] = React.useState(null);

  const sortOptions = [
    { value: "School", label: "School" },
    { value: "Subscription", label: "Plan" },
    { value: "type", label: "Type" },
    { value: "ContactEmail", label: "Email" },
    { value: "OwnStgUrl", label: "Slug" },
    { value: "ExpirationDate", label: "Expiry Date" },
  ];

  const editableRef = useRef(null);
  const clickedSaveRef = React.useRef(false);

  const handleSaveClick = (tenantId, e) => {
    e.stopPropagation();
    clickedSaveRef.current = true; // mark the save was clicked

    setSelectedTenantId(tenantId);
    window.$("#confirmExpiryModal").modal("show");
  };

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (!editingRow) return;

      // Ignore clicks inside the modal
      if (
        document.getElementById("confirmExpiryModal")?.contains(event.target)
      ) {
        return;
      }

      if (editableRef.current && !editableRef.current.contains(event.target)) {
        setEditingRow(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingRow]);

  // Debounce effect
  React.useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500); // Delay in ms

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  React.useEffect(() => {
    const tkn = window.sessionStorage.getItem("Token");
    const usr = window.sessionStorage.getItem("User");
    if (!tkn || !usr) {
      history.push("/");
    }
  }, [history]);

  const fetchData = () => {
    setLoad(true);
    const token = window.sessionStorage.getItem("Token");

    axios
      .get(`${url}/api/get-plan-expiry`, {
        headers: {
          "x-access-token": token,
        },
        params: {
          searchTerm,
          sortBy,
          order,
        },
      })
      .then((res) => {
        if (res.data?.status === "Success") {
          let filtered = res.data.planData.filter(
            (item) => item.Subscription && item.ExpirationDate
          );
          
          // Aplicar filtro por tipo (Trial/Real)
          if (filterType !== "all") {
            filtered = filtered.filter((item) => {
              const isTrial = !item.SubscriptionId || item.SubscriptionId.trim() === "";
              return filterType === "trial" ? isTrial : !isTrial;
            });
          }
          
          // Si el ordenamiento es por "type" (Trial/Real), hacerlo en el frontend
          if (sortBy === "type") {
            filtered = [...filtered].sort((a, b) => {
              const aIsTrial = !a.SubscriptionId || a.SubscriptionId.trim() === "";
              const bIsTrial = !b.SubscriptionId || b.SubscriptionId.trim() === "";
              const aType = aIsTrial ? "Trial" : "Real";
              const bType = bIsTrial ? "Trial" : "Real";
              
              if (order === "desc") {
                return bType.localeCompare(aType);
              } else {
                return aType.localeCompare(bType);
              }
            });
          }
          
          setSubscriptionData(filtered);
        } else {
          setSubscriptionData([]);
        }
      })
      .catch((err) => {
        console.error(err);
        if (err?.response?.status === 401) {
          history.push("/");
          sessionStorage.clear();
        }
      })
      .finally(() => {
        setLoad(false);
      });
  };

  // Trigger API when debounced searchTerm or sorting changes
  React.useEffect(() => {
    fetchData();
  }, [searchTerm, sortBy, order, filterType]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatBackendDate = (date) => {
    return new Date(date).toISOString().split("T")[0]; // YYYY-MM-DD
  };

  const handleEditClick = (item) => {
    setEditingRow(item.TenantID);
    setEditedDate({
      ...editedDate,
      [item.TenantID]: item.ExpirationDate?.split("T")[0] || "",
    });
  };

  return (
    <>
      <div className="mx-4" style={{ marginTop: "0px" }}>
        {msgData ? (
          <div
            className={
              msgData?.type === "Success"
                ? "alert alert-success fixed-bottom alert-dismissible fade show"
                : "alert alert-danger fixed-bottom alert-dismissible fade show"
            }
            role="alert"
            style={{ zIndex: "9999" }}
          >
            {msgData?.message}
            <button
              type="button"
              className="close"
              onClick={() =>
                document
                  .getElementsByClassName("alert")[0]
                  ?.classList.remove("show")
              }
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        ) : null}

        <div className="d-flex justify-content-between align-items-center py-2">
          <h5 className="section-title">Plan Expiry</h5>
          <div className="d-flex gap-3 align-items-center">
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <TextField
              select
              size="small"
              label="Type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="real">Real</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Order"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            >
              <MenuItem value="asc">Asc</MenuItem>
              <MenuItem value="desc">Desc</MenuItem>
            </TextField>
          </div>
        </div>

        {load ? (
          <div className="loader text-center py-5">
            <CircularProgress />
          </div>
        ) : subscriptionData && subscriptionData.length > 0 ? (
          <div className="mx-4">
            <table className="table table-hover text-left">
              <thead>
                <tr>
                  <th>#</th>
                  <th>School</th>
                  <th>Plan</th>
                  <th>Contact Email</th>
                  <th>Slug</th>
                  <th>Plan Expiration Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subscriptionData.map((item, key) => {
                  // Determinar si es Trial o Plan Real basado en SubscriptionId
                  const isTrial = !item.SubscriptionId || item.SubscriptionId.trim() === "";
                  const planType = isTrial ? "Trial" : "Real";
                  
                  return (
                  <tr
                    key={key}
                    ref={editingRow === item.TenantID ? editableRef : null}
                  >
                    <td>{key + 1}</td>
                    <td>{item.School || "—"}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>{item.Subscription}</span>
                        <span
                          className="badge badge-primary"
                          style={{
                            fontSize: "0.7rem",
                            padding: "2px 6px",
                            fontWeight: "500",
                            backgroundColor: isTrial ? "#2196f3" : "#1976d2",
                            color: "#ffffff",
                          }}
                        >
                          {planType}
                        </span>
                      </div>
                    </td>
                    <td>
                      <a
                        href={`mailto:${item.ContactEmail}`}
                        className="text-muted"
                      >
                        {item.ContactEmail}
                      </a>
                    </td>
                    <td>{item.OwnStgUrl}</td>
                    <td>
                      {editingRow === item.TenantID ? (
                        <input
                          type="date"
                          value={editedDate[item.TenantID]}
                          onChange={(e) =>
                            setEditedDate({
                              ...editedDate,
                              [item.TenantID]: e.target.value,
                            })
                          }
                          className="form-control form-control-sm"
                          style={{ maxWidth: "150px" }}
                        />
                      ) : (
                        <span>
                          {item.ExpirationDate
                            ? formatDate(item.ExpirationDate)
                            : "—"}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingRow === item.TenantID ? (
                        <div className="d-flex gap-2">
                          <button
                            onClick={(e) => handleSaveClick(item.TenantID, e)}
                            className="btn btn-sm d-flex align-items-center no-focus-outline"
                            style={{
                              border: "none",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            <EventAvailableOutlined
                              style={{ fontSize: "16px", marginRight: "4px" }}
                            />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingRow(null)}
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                            style={{
                              fontSize: "12px",
                              padding: "4px 8px",
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(item)}
                          className="btn btn-sm d-flex align-items-center no-focus-outline"
                          style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "4px 8px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          <EditCalendar
                            style={{ fontSize: "16px", marginRight: "4px" }}
                          />
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-5">
            <Typography variant="body1" color="textSecondary">
              No data found.
            </Typography>
          </div>
        )}
        <div
          className="modal fade"
          id="confirmExpiryModal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="confirmExpiryModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="confirmExpiryModalLabel">
                  Confirm Expiry Date Update
                </h5>
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                  disabled={loadingConfirm}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body text-left">
                Are you sure you want to update the expiry date?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-dismiss="modal"
                  disabled={loadingConfirm}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: "#D3313A", borderColor: "#D3313A" }}
                  disabled={loadingConfirm}
                  onClick={async () => {
                    setLoadingConfirm(true);
                    const newDate = editedDate[selectedTenantId];
                    try {
                      await axios.put(
                        `${url}/api/update-expiry-date`,
                        {
                          tenantId: selectedTenantId,
                          newExpiryDate: newDate,
                        },
                        {
                          headers: {
                            "x-access-token": sessionStorage.getItem("Token"),
                          },
                        }
                      );

                      // Update local data
                      setSubscriptionData((prev) =>
                        prev.map((entry) =>
                          entry.TenantID === selectedTenantId
                            ? { ...entry, ExpirationDate: newDate }
                            : entry
                        )
                      );

                      // Show success message
                      setMsgData({
                        type: "Success",
                        message: "Expiry date updated successfully!",
                      });

                      // Cleanup
                      setSelectedTenantId(null);
                      setEditingRow(null);
                      window.$("#confirmExpiryModal").modal("hide");
                    } catch (err) {
                      console.error("Error updating expiry:", err);
                      setMsgData({
                        type: "Error",
                        message: "Failed to update expiry date.",
                      });
                    } finally {
                      setLoadingConfirm(false);
                      // Auto-hide alert after 5 seconds
                      setTimeout(() => {
                        document
                          .getElementsByClassName("alert")[0]
                          ?.classList.remove("show");
                      }, 5000);
                    }
                  }}
                >
                  {loadingConfirm ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>{" "}
                      Updating...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlanControl;
